import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ServiceDeskAPI } from '@sd/services/APIs';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { CreatedDateFilterSelect } from '@sd/components/common/CreatedDateFilterSelect';
import {
  emptyCreatedDateFilter,
  hasCreatedDateFilter,
  normalizeCreatedDateFilter,
  type CreatedDateFilterState,
} from '@sd/lib/ticketCreatedDateFilter';
import {
  Clock, Loader2,
  Edit3, Plus, ArrowUpRight, AlertTriangle,
  Users, Zap, CheckCircle, XCircle,
  GitMerge, UserPlus, RotateCcw, Tag, Lock,
  Inbox, RefreshCw, Star, Paperclip, MessageSquare,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { TablePager } from "@sd/components/common/TablePager";

type ActivityEntry = {
  id: string;
  activity_title: string;
  activity_description: string;
  activity_type: string;
  created_on: string;
  created_by: string;
};

/** Derive icon + color from activity_title / activity_type */
function resolveConfig(title: string, type: string) {
  const t = (title + ' ' + type).toLowerCase();

  // Status transitions — check most specific first
  if (t.includes('status updated to resolved') || t.includes('updated to resolved'))
    return { icon: CheckCircle,   color: 'text-green-600 bg-green-100 dark:bg-green-900/30',    label: 'Resolved'   };
  if (t.includes('status updated to closed') || t.includes('updated to closed'))
    return { icon: XCircle,       color: 'text-gray-500 bg-gray-100 dark:bg-gray-800/40',       label: 'Closed'     };
  if (t.includes('status updated to pending') || t.includes('updated to pending'))
    return { icon: Clock,         color: 'text-amber-500 bg-amber-100 dark:bg-amber-900/30',    label: 'Pending'    };
  if (t.includes('status updated to open') || t.includes('updated to open'))
    return { icon: Inbox,         color: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30',       label: 'Opened'     };
  if (t.includes('reopen'))
    return { icon: RotateCcw,     color: 'text-sky-500 bg-sky-100 dark:bg-sky-900/30',          label: 'Reopened'   };

  // Assignment
  if (t.includes('assigned to') || t.includes('unassigned'))
    return { icon: UserPlus,      color: 'text-purple-500 bg-purple-100 dark:bg-purple-900/30', label: 'Assigned'   };

  // Priority change
  if (t.includes('priority'))
    return { icon: Tag,           color: 'text-orange-500 bg-orange-100 dark:bg-orange-900/30', label: 'Priority'   };

  // Escalation
  if (t.includes('escalat'))
    return { icon: ArrowUpRight,  color: 'text-red-500 bg-red-100 dark:bg-red-900/30',          label: 'Escalated'  };

  // SLA
  if (t.includes('sla breach') || t.includes('sla updated') || t.includes('sla rule'))
    return { icon: AlertTriangle, color: 'text-yellow-500 bg-yellow-100 dark:bg-yellow-900/30', label: 'SLA'        };

  // Merge
  if (t.includes('merge'))
    return { icon: GitMerge,      color: 'text-indigo-500 bg-indigo-100 dark:bg-indigo-900/30', label: 'Merge'      };

  // Attachments
  if (t.includes('attachment'))
    return { icon: Paperclip,     color: 'text-teal-500 bg-teal-100 dark:bg-teal-900/30',       label: 'Attachment' };

  // Notes / Comments
  if (t.includes('note') || t.includes('comment'))
    return { icon: MessageSquare, color: 'text-cyan-500 bg-cyan-100 dark:bg-cyan-900/30',        label: 'Note'       };

  // CSAT / Rating
  if (t.includes('csat') || t.includes('rating'))
    return { icon: Star,          color: 'text-yellow-500 bg-yellow-100 dark:bg-yellow-900/30', label: 'CSAT'       };

  // VIP
  if (t.includes('vip'))
    return { icon: Lock,          color: 'text-amber-600 bg-amber-100 dark:bg-amber-900/30',    label: 'VIP'        };

  // Generic status update
  if (t.includes('status updated') || t.includes('status was updated'))
    return { icon: RefreshCw,     color: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30',       label: 'Updated'    };

  // Created
  if (t.includes('new ') || t.includes('creat') || type.toLowerCase() === 'create')
    return { icon: Plus,          color: 'text-green-600 bg-green-100 dark:bg-green-900/30',    label: 'Created'    };

  // Generic edit
  if (t.includes('edit') || t.includes('updat') || type.toLowerCase() === 'edit')
    return { icon: Edit3,         color: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30',       label: 'Edited'     };

  // User actions
  if (t.includes('user') || t.includes('deactivat') || t.includes('activat'))
    return { icon: Users,         color: 'text-purple-500 bg-purple-100 dark:bg-purple-900/30', label: 'User'       };

  return { icon: Zap, color: 'text-muted-foreground bg-muted', label: type || 'System' };
}

/** Extract TXT-XXXXX from title */
function extractTicketId(title: string): string | null {
  const m = title.match(/\[?(TXT-\d+)\]?/i);
  return m ? m[1] : null;
}

/** Extract Previous → New status from activity_description */
function extractStatusChange(desc: string): { prev: string; next: string } | null {
  const prev = desc.match(/Previous status:\s*(\S+)/i)?.[1];
  const next = desc.match(/New status:\s*(\S+)/i)?.[1];
  if (prev && next) return { prev, next };
  return null;
}

/** Extract a field value from description block */
function extractDescField(desc: string, field: string): string | null {
  const m = desc.match(new RegExp(`${field}:\\s*(.+)`, 'i'));
  return m ? m[1].trim() : null;
}

function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function parseEntries(raw: unknown): ActivityEntry[] {
  if (!raw) return [];
  const arr = Array.isArray(raw)
    ? raw
    : Array.isArray((raw as any)?.activity_logs)
      ? (raw as any).activity_logs
      : Array.isArray((raw as any)?.results)
        ? (raw as any).results
        : Array.isArray((raw as any)?.data)
          ? (raw as any).data
          : [];
  return arr.map((r: any) => ({
    id:                   String(r.id ?? ''),
    activity_title:       String(r.activity_title  ?? r.title  ?? r.action  ?? ''),
    activity_description: String(r.activity_description ?? r.description ?? r.details ?? ''),
    activity_type:        String(r.activity_type   ?? r.type   ?? ''),
    created_on:           String(r.created_on      ?? r.created_at ?? r.performed_at ?? r.timestamp ?? ''),
    created_by:           String(r.created_by      ?? r.performed_by ?? r.user ?? ''),
  }));
}


export function AdminActivityLog() {
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [createdDateFilter, setCreatedDateFilter] =
    useState<CreatedDateFilterState>(emptyCreatedDateFilter);

  const apiParams = useMemo(() => {
    const p: Record<string, unknown> = {
      page: pageNum,
      page_size: pageSize,
    };
    const { from, to } = normalizeCreatedDateFilter(createdDateFilter);
    if (from) p.start_date = from;
    if (to) p.end_date = to;
    return p;
  }, [pageNum, pageSize, createdDateFilter]);

  const handleDateFilterChange = (next: CreatedDateFilterState) => {
    setCreatedDateFilter(next);
    setPageNum(1);
  };

  const { data: rawData, isPending, isError } = useQuery({
    queryKey: ['sd-activity-logs', apiParams],
    queryFn: () =>
      ServiceDeskAPI.activityLogs.list(apiParams).then(r => r.data),
    staleTime: 30_000,
    retry: 1,
  });

  const allEntries = useMemo(() => parseEntries(rawData), [rawData]);

  const totalCount: number =
    (rawData as any)?.count ??
    (rawData as any)?.total ??
    (rawData as any)?.total_count ??
    allEntries.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const filtered = allEntries;

  const hasActiveFilter = hasCreatedDateFilter(createdDateFilter);

  return (
    <div className="min-w-0 p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Activity Log</h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            Complete audit trail of all helpdesk actions
          </p>
        </div>

        <div className="flex w-full shrink-0 flex-col items-stretch gap-2 sm:w-auto sm:items-end">
          <div className="flex flex-col gap-1 sm:items-end">
            <span className="text-xs font-medium text-muted-foreground">
              Date range
            </span>
            <CreatedDateFilterSelect
              value={createdDateFilter}
              onChange={handleDateFilterChange}
              responsive
            />
          </div>
          {hasActiveFilter ? (
            <Badge
              variant="secondary"
              className="self-end px-3 py-1 text-xs font-medium"
            >
              {createdDateFilter.from && createdDateFilter.to
                ? `${createdDateFilter.from} → ${createdDateFilter.to}`
                : createdDateFilter.from
                  ? `From ${createdDateFilter.from}`
                  : `Until ${createdDateFilter.to}`}
            </Badge>
          ) : null}
        </div>
      </div>

      {isError && (
        <p className="text-sm text-destructive">Failed to load activity logs.</p>
      )}

      {/* Timeline */}
      {isPending ? (
        <div className="flex items-center justify-center py-24 gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading activity logs…</span>
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground text-sm">
            No activity entries found.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1">
            {filtered.map(entry => {
              const { icon: Icon, color, label } = resolveConfig(entry.activity_title, entry.activity_type);
              const ticketId     = extractTicketId(entry.activity_title);
              const statusChange = extractStatusChange(entry.activity_description);
              const subject      = extractDescField(entry.activity_description, 'Subject');
              const priority     = extractDescField(entry.activity_description, 'Priority');
              const category     = extractDescField(entry.activity_description, 'Category');
              const dateStr      = entry.created_on
                ? new Date(entry.created_on).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                : '';

              const statusColor: Record<string, string> = {
                open:     'bg-blue-100 text-blue-700',
                pending:  'bg-yellow-100 text-yellow-700',
                resolved: 'bg-green-100 text-green-700',
                closed:   'bg-gray-100 text-gray-600',
              };

              return (
                <div
                  key={entry.id}
                  className="relative flex items-start gap-3 rounded-lg p-2.5 transition-colors hover:bg-muted/30 sm:gap-4 sm:p-3"
                >
                  <div className={cn("z-10 flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full", color)}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    {/* Title row */}
                    <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
                      <span
                        className="min-w-0 flex-1 text-sm font-semibold leading-snug line-clamp-2 break-words"
                        title={entry.activity_title}
                      >
                        {entry.activity_title}
                      </span>
                      <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 py-0">{label}</Badge>
                      {ticketId && (
                        <span className="shrink-0 whitespace-nowrap font-mono text-xs font-semibold text-primary">
                          {ticketId}
                        </span>
                      )}
                    </div>

                    {/* Status change pill */}
                    {statusChange && (
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        <span className={cn("text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize", statusColor[statusChange.prev] ?? 'bg-muted text-muted-foreground')}>
                          {statusChange.prev}
                        </span>
                        <span className="text-xs text-muted-foreground">→</span>
                        <span className={cn("text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize", statusColor[statusChange.next] ?? 'bg-muted text-muted-foreground')}>
                          {statusChange.next}
                        </span>
                      </div>
                    )}

                    {/* Key details row */}
                    {(subject || priority || category) && (
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        {subject  && (
                          <span
                            className="min-w-0 max-w-full break-words leading-snug line-clamp-2"
                            title={subject}
                          >
                            <span className="font-medium text-foreground/70">Subject:</span> {subject}
                          </span>
                        )}
                        {priority && <span className="break-words"><span className="font-medium text-foreground/70">Priority:</span> {priority}</span>}
                        {category && <span className="break-words"><span className="font-medium text-foreground/70">Category:</span> {category}</span>}
                      </div>
                    )}

                    {/* Timestamp */}
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground flex-wrap">
                      {dateStr && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {dateStr}
                        </span>
                      )}
                      {entry.created_on && (
                        <span className="text-muted-foreground/60">({timeAgo(entry.created_on)})</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {/* Pagination */}
      {!isPending && totalCount > 0 && (
        <div className="overflow-x-auto">
          <TablePager
            pageNum={pageNum}
            totalPages={totalPages}
            pageSize={pageSize}
            isLoading={isPending}
            onPageChange={setPageNum}
            onPageSizeChange={size => { setPageSize(size); setPageNum(1); }}
            pageSizeOptions={[20, 50, 100, 200]}
          />
        </div>
      )}
    </div>
  );
}
